import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import axios from 'axios';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [token, setToken]     = useState(() => sessionStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [initialized, setInitialized] = useState(false);

  // On utilise l'URL de l'API définie dans les variables d'environnement
  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const api = useMemo(() => {
    const inst = axios.create({
      baseURL,
    });

    inst.interceptors.request.use(config => {
      console.log('→ API request:', config.baseURL + config.url);
      const t = sessionStorage.getItem('token');
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });

    inst.interceptors.response.use(
      res => res,
      err => {
        const st = err.response?.status;
        const url = err.config?.url || '';
        if (initialized && (st === 401 || st === 403) && 
            !url.includes('/users/profile') && 
            !url.includes('/users/verify-password')) {
          sessionStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
        return Promise.reject(err);
      }
    );

    return inst;
  }, [baseURL, initialized]);

  const refreshToken = useCallback(async () => {
    try {
      const stored = sessionStorage.getItem('token');
      if (!stored) return;
      const { data } = await api.get('/users/profile');
      if (data.status === 'success') {
        setUser(data.data.user);
        setToken(stored);
      } else {
        sessionStorage.removeItem('token');
      }
    } catch {
      sessionStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [api]);

  useEffect(() => {
    refreshToken();
  }, [refreshToken]);

  const login = async (email, password, recaptchaToken) => {
    setLoading(true);
    setError(null);
    try {
      console.log('Login attempt:', { email, recaptchaToken: Boolean(recaptchaToken) });
      const { data } = await api.post('/users/login', {
        email, password, recaptchaToken
      });

      console.log('Login response:', data);
      const tokenValue = data.token ?? data.data?.token;
      const userData   = data.user  ?? data.data?.user;

      if (!tokenValue) throw new Error('Aucun token reçu du serveur');

      sessionStorage.setItem('token', tokenValue);
      setToken(tokenValue);
      setUser(userData);
      return { success: true };
    } catch (err) {
      console.error('Connexion error:', err);
      let msg = err.response?.data?.message || err.message || 'Erreur lors de la connexion';
      if (err.response?.status === 503) {
        msg = 'Service indisponible, réessayez plus tard.';
      }
      setError(msg);
      return {
        success: false,
        message: msg,
        remainingAttempts: err.response?.data?.remainingAttempts
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setError(null);
  };

  const register = async userData => {
    try {
      const { data } = await api.post('/users/register', userData);
      const newToken = data.token ?? data.data?.token;
      if (newToken) {
        sessionStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(data.data?.user);
        setError(null);
      }
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Erreur lors de l\'inscription';
      setError(msg);
      throw err;
    }
  };

  const updateProfile = async userData => {
    try {
      const { data } = await api.put('/users/profile', userData);
      if (data.status === 'success') {
        setUser(data.data.user);
        setError(null);
      }
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Erreur lors de la mise à jour du profil';
      setError(msg);
      throw err;
    }
  };

  // Fonction pour rafraîchir les données utilisateur après une modification par un administrateur
  const refreshUserData = useCallback(async () => {
    if (token) {
      try {
        const { data } = await api.get('/users/profile');
        if (data.status === 'success') {
          setUser(data.data.user);
        }
        return data;
      } catch (err) {
        console.error('Erreur lors du rafraîchissement des données utilisateur:', err);
      }
    }
  }, [api, token]);

  return (
    <AuthContext.Provider value={{
      user, token, loading, error, setError,
      login, logout, register, updateProfile, refreshToken, refreshUserData, api
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;